import { z } from "zod";
/**
 * The written second opinion. This is the artifact the patient is actually
 * buying, so it is a structured record rather than a free-text note: it can be
 * rendered consistently, summarised in an email, turned into a PDF, and - later
 * - aggregated to show which original diagnoses the service most often
 * disagrees with, which is a genuinely interesting number for this business.
 */
/** The heart of a second opinion: does the reviewing specialist agree? */
export const CONCURRENCE = ["agree", "agree_with_reservations", "disagree", "inconclusive"];
export const concurrenceSchema = z.enum(CONCURRENCE);
/** How quickly the patient should act on this. Drives the email subject line. */
export const URGENCY = ["routine", "prompt", "urgent"];
export const urgencySchema = z.enum(URGENCY);
export const recommendedActionSchema = z.object({
    action: z.string().trim().min(3).max(300),
    rationale: z.string().trim().min(3).max(1000),
});
export const outcomeSchema = z.object({
    /** What the specialist understood the case to be. Confirms they read the file. */
    case_summary: z.string().trim().min(20).max(4000),
    concurrence: concurrenceSchema,
    /** Required when the specialist does not simply agree - a bare "disagree" is not an opinion. */
    concurrence_rationale: z.string().trim().max(4000).optional(),
    findings: z.string().trim().min(20).max(8000),
    recommended_actions: z.array(recommendedActionSchema).min(1).max(10),
    /** Further imaging, labs or referrals the specialist wants before deciding. */
    additional_investigations: z.array(z.string().trim().min(3).max(300)).max(10).default([]),
    urgency: urgencySchema,
    /**
     * S3 keys of the records this opinion was based on. Recording what was
     * actually reviewed is what makes the opinion defensible later.
     */
    reviewed_document_keys: z.array(z.string()).default([]),
});
/**
 * A specialist who does not fully agree with the original diagnosis has to say
 * why. This is the single most consequential field in the document and an
 * unexplained disagreement is worse than useless to the patient.
 */
export const outcomeSubmissionSchema = outcomeSchema.superRefine((value, ctx) => {
    const needsRationale = ["agree_with_reservations", "disagree", "inconclusive"];
    if (!needsRationale.includes(value.concurrence))
        return;
    const rationale = value.concurrence_rationale?.trim() ?? "";
    if (rationale.length < 20) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["concurrence_rationale"],
            message: `A concurrence of "${value.concurrence}" requires a rationale of at least 20 characters.`,
        });
    }
});
export function concurrenceLabel(value) {
    switch (value) {
        case "agree":
            return "Agrees with the original diagnosis";
        case "agree_with_reservations":
            return "Agrees, with reservations";
        case "disagree":
            return "Disagrees with the original diagnosis";
        case "inconclusive":
            return "Unable to reach a conclusion on the available records";
    }
}
export function urgencyLabel(value) {
    switch (value) {
        case "routine":
            return "Routine - no immediate action required";
        case "prompt":
            return "Prompt - act within the coming weeks";
        case "urgent":
            return "Urgent - seek care promptly";
    }
}
//# sourceMappingURL=outcome.js.map